// React Query hooks for the leader feature: the people a leader leads, and their combined results.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  LEADER_INSIGHTS_POLL_INTERVAL_MS,
  LEADER_INSIGHTS_REFETCH_INTERVAL_MS
} from '../../../../pure-js/libs/leaderInsights'
import type { SetLeaderScopeRequest } from '../../../../pure-js/types/Leader'
import { getActiveOrganizationId } from '../../features/organization/orgStateHelpers'
import useAppState from '../../hooks/useAppState'
import { QUERY_KEYS } from '../queryKeys'
import { getLeaderInsights, getLeaderScope, setLeaderScope } from '../processHandlers/leader'

type LeaderQueryOptions = { enabled?: boolean }

export const useLeaderScope = (
  organizationId: string | undefined,
  leaderUserId: string | undefined,
  options?: LeaderQueryOptions
) => {
  const hasTarget = !!organizationId && !!leaderUserId

  return useQuery({
    queryKey: QUERY_KEYS.LEADER_SCOPE(organizationId, leaderUserId),
    queryFn: () => getLeaderScope({ organizationId, leaderUserId: leaderUserId as string }),
    staleTime: 0,
    enabled: hasTarget && (options?.enabled ?? true)
  })
}

// A new scope changes whose results are combined, so the leader's insights are stale with it.
export const useSetLeaderScope = () => {
  const queryClient = useQueryClient()
  const { state } = useAppState()

  return useMutation({
    mutationFn: (request: SetLeaderScopeRequest) => setLeaderScope(request),
    onSuccess: (_response, request) => {
      const organizationId = request.organizationId ?? getActiveOrganizationId(state.user)
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.LEADER_SCOPE(organizationId, request.leaderUserId) })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.LEADER_INSIGHTS(organizationId, request.leaderUserId) })
    }
  })
}

type LeaderInsightsQueryOptions = {
  /** Whose results are read; without it, the signed-in user's own. */
  leaderUserId?: string
  enabled?: boolean
  /** The open My people page asks for this; the shell only reads whether the feature is on and must not poll on every page. */
  shouldRefetchWhileOpen?: boolean
}

// Reads fast while the results are being rebuilt, slowly otherwise, and never from a tab nobody is looking at.
const resolveInsightsRefetchIntervalMs = (isRefreshing: boolean | undefined): number =>
  isRefreshing ? LEADER_INSIGHTS_POLL_INTERVAL_MS : LEADER_INSIGHTS_REFETCH_INTERVAL_MS

export const useLeaderInsights = (options?: LeaderInsightsQueryOptions) => {
  const { state } = useAppState()
  const organizationId = getActiveOrganizationId(state.user)
  const leaderUserId = options?.leaderUserId ?? state.user?.id

  return useQuery({
    queryKey: QUERY_KEYS.LEADER_INSIGHTS(organizationId, leaderUserId),
    queryFn: () => getLeaderInsights({ organizationId, leaderUserId: options?.leaderUserId }),
    staleTime: 0,
    enabled: !!state.user?.id && (options?.enabled ?? true),
    refetchInterval: (query) => {
      if (!options?.shouldRefetchWhileOpen) return false

      return resolveInsightsRefetchIntervalMs(query.state.data?.data?.isRefreshing)
    },
    refetchIntervalInBackground: false
  })
}
